"use client";

import React, { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";

interface ThemeComponentProps {
  children: ReactNode;
  className?: string;
}

interface ThemeBackgroundProps extends ThemeComponentProps {
  variant?: "default" | "mesh" | "grid" | "dots";
}

export function ThemeBackground({
  children,
  className,
  variant = "default"
}: ThemeBackgroundProps) {
  const { isDark } = useTheme();

  const bg = (() => {
    if (variant === "mesh") return isDark ? "bg-mesh-dark" : "bg-mesh-light";
    if (variant === "grid") return isDark ? "bg-[#07070B] bg-grid" : "bg-[#FAFAF9] bg-grid";
    if (variant === "dots") return isDark ? "bg-[#07070B] bg-dots" : "bg-[#FAFAF9] bg-dots";
    return isDark ? "bg-[#07070B]" : "bg-[#FAFAF9]";
  })();

  return (
    <div
      className={cn(
        "transition-colors text-foreground antialiased",
        bg,
        className
      )}
    >
      {children}
    </div>
  );
}

interface ThemeSectionProps extends ThemeComponentProps {
  id?: string;
  variant?: "default" | "alternate";
  pattern?: "none" | "grid" | "dots";
}

export function ThemeSection({
  children,
  className,
  id,
  variant = "default",
  pattern = "none"
}: ThemeSectionProps) {
  const { isDark } = useTheme();

  const bgColor =
    variant === "default"
      ? isDark ? "bg-[#07070B]" : "bg-[#FAFAF9]"
      : isDark ? "bg-[#0B0B10]" : "bg-white";

  return (
    <section
      id={id}
      className={cn("py-24 sm:py-32 relative overflow-hidden", bgColor, className)}
    >
      {pattern !== "none" && (
        <div
          className={cn(
            "pointer-events-none absolute inset-0 -z-0",
            pattern === "grid"
              ? isDark ? "bg-grid opacity-50" : "bg-grid opacity-60"
              : isDark ? "bg-dots opacity-40" : "bg-dots opacity-50"
          )}
        />
      )}
      <div className="relative z-10">{children}</div>
    </section>
  );
}

interface ThemeCardProps extends ThemeComponentProps {
  hover?: boolean;
  variant?: "solid" | "glass" | "gradient";
}

export function ThemeCard({
  children,
  className,
  hover = false,
  variant = "glass"
}: ThemeCardProps) {
  const { isDark } = useTheme();

  const variantClasses =
    variant === "solid"
      ? isDark
        ? "bg-[#0E0E14] border border-white/5"
        : "bg-white border border-stone-200"
      : variant === "gradient"
      ? isDark
        ? "bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10"
        : "bg-gradient-to-br from-white to-stone-50 border border-stone-200"
      : isDark
      ? "glass"
      : "bg-white/70 backdrop-blur-xl border border-stone-200/80";

  return (
    <div
      className={cn(
        "rounded-3xl transition-all duration-300",
        variantClasses,
        hover && isDark && "hover:border-brand-400/40 hover:shadow-glow",
        hover && !isDark && "hover:border-brand-300/60 hover:shadow-elevated",
        className
      )}
    >
      {children}
    </div>
  );
}

interface ThemeTextProps extends ThemeComponentProps {
  variant?: "default" | "subtle" | "muted";
  as?: "p" | "span" | "div";
}

export function ThemeText({
  children,
  className,
  variant = "default",
  as: Component = "p"
}: ThemeTextProps) {
  const { isDark } = useTheme();

  const textClasses = (() => {
    switch (variant) {
      case "subtle":
        return isDark ? "text-white/70" : "text-stone-600";
      case "muted":
        return isDark ? "text-white/50" : "text-stone-500";
      default:
        return isDark ? "text-white" : "text-stone-900";
    }
  })();

  return <Component className={cn(textClasses, className)}>{children}</Component>;
}

/* ---------- Decorative backgrounds ---------- */

interface ThemeGradientBackgroundProps {
  position?: "center" | "top-right" | "bottom-left" | "bottom-right" | "top-left";
  intensity?: "normal" | "strong";
}

export function ThemeGradientBackground({
  position = "center",
  intensity = "normal"
}: ThemeGradientBackgroundProps) {
  const { isDark } = useTheme();
  const o = intensity === "strong" ? (isDark ? 0.22 : 0.16) : isDark ? 0.16 : 0.12;

  const stops = (() => {
    switch (position) {
      case "top-right":
        return `radial-gradient(circle_at_top_right, rgba(167,139,250,${o}), transparent 55%), radial-gradient(circle_at_top_right, rgba(34,211,238,${o * 0.6}), transparent 60%)`;
      case "top-left":
        return `radial-gradient(circle_at_top_left, rgba(244,114,182,${o}), transparent 55%), radial-gradient(circle_at_top_left, rgba(167,139,250,${o * 0.7}), transparent 65%)`;
      case "bottom-left":
        return `radial-gradient(circle_at_bottom_left, rgba(167,139,250,${o}), transparent 55%)`;
      case "bottom-right":
        return `radial-gradient(circle_at_bottom_right, rgba(244,114,182,${o * 0.7}), transparent 60%), radial-gradient(circle_at_bottom_right, rgba(34,211,238,${o * 0.5}), transparent 65%)`;
      default:
        return `radial-gradient(circle_at_center, rgba(167,139,250,${o}), transparent 60%)`;
    }
  })();

  return (
    <div
      className="pointer-events-none absolute inset-0 -z-0"
      style={{ backgroundImage: stops }}
    />
  );
}

interface AuroraProps {
  variant?: "violet" | "cyan" | "pink" | "mixed";
}

export function AuroraBlobs({ variant = "mixed" }: AuroraProps) {
  const { isDark } = useTheme();

  const colors = (() => {
    if (variant === "violet")
      return ["bg-brand-700/35", "bg-brand-500/25"];
    if (variant === "cyan")
      return ["bg-brand-500/35", "bg-brand-700/25"];
    if (variant === "pink")
      return ["bg-brand-800/35", "bg-brand-600/25"];
    return ["bg-brand-700/35", "bg-brand-500/25", "bg-brand-800/25"];
  })();

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden -z-0 motion-reduce:hidden"
      aria-hidden="true"
    >
      {/* Each blob is a wrapper that floats; the blurred, expensive div is its child,
          so the GPU only has to re-blur a smaller surface and the float is on a
          dedicated translate3d layer. */}
      <div className="absolute -top-32 -left-24 h-[420px] w-[420px] aurora-float">
        <div
          className={cn(
            "h-full w-full rounded-full aurora-blob",
            colors[0]
          )}
        />
      </div>
      <div className="absolute top-1/3 -right-24 h-[380px] w-[380px] aurora-float [animation-delay:-3s]">
        <div
          className={cn(
            "h-full w-full rounded-full aurora-blob",
            colors[1] ?? colors[0]
          )}
        />
      </div>
      {variant === "mixed" && (
        <div className="absolute -bottom-40 left-1/4 h-[440px] w-[440px] aurora-float [animation-delay:-6s]">
          <div
            className={cn(
              "h-full w-full rounded-full aurora-blob",
              colors[2]
            )}
          />
        </div>
      )}
    </div>
  );
}
